import { Autocomplete } from '@features/common';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import {
	ProcessEditionFormType,
	ProcessGuidesRangeFormType,
} from '@features/processes/interfaces/process-edition.interface';
import { AddGuidesRangeForm, ProcessRangeHistoryCard } from '@features/ranges';
import useFirebase from '@hooks/firebase.hook';
import { ClienteSuite, DivisionCliente } from '@interfaces/firestore-documents.interfaces';
import { ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Checkbox,
	Divider,
	Grid,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import {
	handleAddGuideRangeForm,
	handleProcessNameChange,
	handleProcessNameKeyPress,
	handleProcessNamePaste,
	handleTopicPushChange,
	handleTopicPushKeyPress,
	handleTopicPushPaste,
} from './ProcessEditionForm.handlers';
import useProcessEditionFormStyles from './ProcessEditionForm.styles';

interface Props {
	defaultState: boolean;
	division: DivisionCliente;
	cities: Cities[];
	form: UseFormReturn<ProcessEditionFormType>;
	id_proceso: string;
	allData: ClienteSuite | null;
	getDataById: (identificacion: string) => Promise<void>;
}

const ProcessEditionForm: React.FC<Props> = ({
	defaultState,
	division: { combos: combosDivisionC, niveles_servicio: servicesDivision },
	form,
	cities,
	id_proceso,
	allData,
	getDataById,
}) => {
	const { control, watch } = form;

	// States
	const [isAddGuidesRangeFormOpen, setIsAddGuidesRangeFormOpen] = useState<boolean>(false);
	// Hooks
	const processRanges = useFieldArray({ control, name: 'ranges' });
	const { combos, serviceLevels } = useFirebase();
	const classes = useProcessEditionFormStyles();
	const combosDivision = combosDivisionC.map((c) => Number(c.id_combo));
	const clienteGeneraNumeracion = watch('cliente_genera_numeracion');
	// const consecutivoDesde = watch('consecutivo_desde') || 0;
	// const consecutivoHasta = watch('consecutivo_hasta') || 1;

	// useEffect(() => {
	// 	setValue('cliente_genera_numeracion', clienteGeneraNumeracion);
	// }, [setValue, clienteGeneraNumeracion]);
	// Variables
	const ProcessStateComponent = (
		<>
			<Typography color="#707070" fontFamily="Montserrat" fontSize={14} fontWeight={600}>
				Configuración del proceso
			</Typography>
			<Grid container alignItems="center" justifyContent="space-between">
				<Grid item>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Estado del proceso
					</Typography>
				</Grid>
				<Grid item>
					<Controller
						control={control}
						name="estado"
						render={({ field: { onChange, value } }) => (
							<Switch id={'en_uso_process'} checked={value} onChange={onChange} />
						)}
					/>
				</Grid>
			</Grid>
		</>
	);

	if (!defaultState) {
		return <div style={{ padding: '0 24px' }}>{ProcessStateComponent}</div>;
	}

	return (
		<>
			{isAddGuidesRangeFormOpen && (
				<AddGuidesRangeForm
					cities={cities}
					allData={allData}
					clienteGeneraNumeracion={clienteGeneraNumeracion}
					onClose={handleAddGuideRangeForm(false, setIsAddGuidesRangeFormOpen)}
					id_proceso={id_proceso || ''}
					InputClienteGenera={
						<Grid
							container
							alignItems="center"
							justifyContent="space-between"
							style={{ marginTop: 8 }}
						>
							<Grid item>
								<Typography
									color="#707070"
									fontFamily="Montserrat"
									fontSize={14}
									fontWeight={500}
								>
									Cliente genera numeración
								</Typography>
							</Grid>
							<Grid item>
								<Controller
									control={control}
									name="cliente_genera_numeracion"
									render={({ field: { onChange, value } }) => (
										<Checkbox checked={value} onChange={onChange} />
									)}
								/>
							</Grid>
						</Grid>
					}
					form={form}
					getDataById={getDataById}
					nit={allData?.identificacion}
				/>
			)}
			<div style={{ padding: '0 24px' }}>
				<Typography color="#707070" fontFamily="Montserrat" fontSize={14} fontWeight={600}>
					Cambiar nombre del proceso
				</Typography>
				<Controller
					control={control}
					name="nombre_proceso"
					render={({ field: { onChange, value } }) => (
						<TextField
							fullWidth
							autoCapitalize="on"
							onChange={handleProcessNameChange(onChange)}
							onKeyPress={handleProcessNameKeyPress}
							onPaste={handleProcessNamePaste}
							placeholder="Nuevo nombre"
							size="small"
							style={{ marginTop: 8 }}
							value={value ?? ''}
						/>
					)}
				/>
				<Typography
					color="#707070"
					fontFamily="Montserrat"
					fontSize={14}
					fontWeight={600}
					style={{ marginTop: 24 }}
				>
					Servicios y Combos asociados
				</Typography>
				<Typography
					color="#707070"
					fontFamily="Montserrat"
					fontSize={14}
					fontWeight={500}
					style={{ marginTop: 16 }}
				>
					Servicios
				</Typography>
				<Controller
					control={control}
					name="servicios_asociados"
					render={({ field: { onChange, value } }) => (
						<Autocomplete
							getTooltipTitle={(value) => serviceLevels[value] ?? ''}
							id="service_level_option"
							options={Object.keys(serviceLevels)
								.map(Number)
								.filter((ns) => servicesDivision.includes(ns))}
							onChange={onChange}
							placeholder="Nuevo servicio"
							style={{ marginTop: 8 }}
							value={value ?? []}
						/>
					)}
				/>
				<Typography
					color="#707070"
					fontFamily="Montserrat"
					fontSize={14}
					fontWeight={500}
					style={{ marginTop: 16 }}
				>
					Combos
				</Typography>
				<Controller
					control={control}
					name="combos_asociados"
					render={({ field: { onChange, value } }) => (
						<Autocomplete
							getTooltipTitle={(value) => combos[value]?.nombre_combo ?? ''}
							id="combo_option"
							options={Object.values(combos)
								.filter(
									(c) => c.estado && combosDivision.includes(Number(c.id_combo))
								)
								.map((c) => c.id_combo)}
							onChange={onChange}
							placeholder="Nuevo combo"
							style={{ marginTop: 8 }}
							value={value ?? []}
						/>
					)}
				/>
				<Typography
					color="#707070"
					fontFamily="Montserrat"
					fontSize={14}
					fontWeight={600}
					style={{ marginTop: 24 }}
				>
					Liquidación y Notificaciones
				</Typography>
			</div>
			<Grid
				container
				alignItems="center"
				justifyContent="space-between"
				style={{ marginTop: 8 }}
			>
				<Grid item style={{ paddingLeft: 24 }}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Liquidación en guía
					</Typography>
				</Grid>
				<Grid item style={{ paddingRight: 24 }}>
					<Controller
						control={control}
						name="liquidacion_en_guia"
						render={({ field: { onChange, value } }) => (
							<Switch checked={value} onChange={onChange} />
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider />
				</Grid>
				<Grid item style={{ paddingLeft: 24 }}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Liquidación en despacho
					</Typography>
				</Grid>
				<Grid item style={{ paddingRight: 24 }}>
					<Controller
						control={control}
						name="liquidacion_en_despacho"
						render={({ field: { onChange, value } }) => (
							<Switch checked={value} onChange={onChange} />
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider />
				</Grid>
				{/* <Grid item style={{ paddingLeft: 24 }}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Notificación a destinatario
					</Typography>
				</Grid>
				<Grid item style={{ paddingRight: 24 }}>
					<Controller
						control={control}
						name="notificacion_destinatario"
						render={({ field: { onChange, value } }) => (
							<Switch checked={value} onChange={onChange} />
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider />
				</Grid> */}
				<Grid item style={{ paddingLeft: 24 }}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={500}
					>
						Cotizador
					</Typography>
				</Grid>
				<Grid item style={{ paddingRight: 24 }}>
					<Controller
						control={control}
						name="cotizador"
						render={({ field: { onChange, value } }) => (
							<Switch checked={value} onChange={onChange} />
						)}
					/>
				</Grid>
			</Grid>
			<Accordion className={classes.accordion}>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={600}
					>
						Configuración avanzada
					</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={18}
						fontWeight={500}
					>
						Rango de guías y Consecutivo
					</Typography>
					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						style={{ marginTop: 8 }}
					>
						<Grid item>
							<Typography
								color="#707070"
								fontFamily="Montserrat"
								fontSize={14}
								fontWeight={500}
							>
								Cliente genera numeración
							</Typography>
						</Grid>
						<Grid item>
							<Controller
								control={control}
								name="cliente_genera_numeracion"
								render={({ field: { onChange, value } }) => (
									<Switch checked={value} onChange={onChange} />
								)}
							/>
						</Grid>
					</Grid>
					<Grid item xs={11.7}>
						<Divider />
					</Grid>
					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						style={{ marginTop: 2 }}
					>
						<Grid item>
							<Typography
								color="#707070"
								fontFamily="Montserrat"
								fontSize={14}
								fontWeight={500}
							>
								Habilitar referencia detalle única
							</Typography>
						</Grid>
						<Grid item>
							<Controller
								control={control}
								name="flag_referencia_unica"
								render={({ field: { onChange, value } }) => (
									<Switch checked={value} onChange={onChange} />
								)}
							/>
						</Grid>
					</Grid>
					{processRanges.fields.map(
						(range: ProcessGuidesRangeFormType, index: number) => {
							return (
								<ProcessRangeHistoryCard
									data={range}
									key={`process-edition-form-${range.id_rango}-${index}`}
									cities={cities}
									form={form}
									index={index}
								/>
							);
						}
					)}
					<Button
						fullWidth
						color="secondary"
						onClick={handleAddGuideRangeForm(true, setIsAddGuidesRangeFormOpen)}
						style={{
							borderColor: 'rgba(0, 0, 0, 0.12)',
							borderRadius: 4,
							boxShadow: '0px 3px 1px #00000033, 0px 2px 2px #00000024',
							color: '#686868',
							fontFamily: 'Montserrat',
							fontSize: 12,
							fontWeight: 'medium',
							height: 32,
							marginTop: 16,
							minWidth: 160,
							padding: '8px 24px',
							textTransform: 'none',
						}}
						variant="outlined"
					>
						Nuevo rango de guías
					</Button>
					<Typography
						color="#707070"
						fontFamily="Montserrat"
						fontSize={14}
						fontWeight={600}
						style={{ marginTop: 24 }}
					>
						Notificación push
					</Typography>
					<Controller
						control={control}
						name="topic_push"
						render={({ field: { onChange, value } }) => (
							<TextField
								fullWidth
								multiline
								minRows={3}
								onChange={handleTopicPushChange(onChange)}
								onKeyPress={handleTopicPushKeyPress}
								onPaste={handleTopicPushPaste}
								style={{ marginTop: 8 }}
								value={value ?? ''}
							/>
						)}
					/>
					<div style={{ marginTop: 24 }}>{ProcessStateComponent}</div>
				</AccordionDetails>
			</Accordion>
		</>
	);
};

export default ProcessEditionForm;
